import { useState, useMemo } from "react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
    MagnifyingGlass,
    ArrowsClockwise,
    Sparkle,
    MapPin,
    ChartBar,
    Trophy,
    Warning,
    Copy,
    ArrowUpRight,
    Info,
    FilePdf,
} from "@phosphor-icons/react";
import { marketResearchAnalyze, keywordPlannerGeoSuggest, keywordPlannerOptions, errText } from "@/lib/api";
import { useEffect } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    ResponsiveContainer,
    Tooltip as RTooltip,
    LabelList,
    Cell,
} from "recharts";

// Reuse the KP language list. Country label used only for AI prompt clarity.
const currency = (n) =>
    typeof n === "number"
        ? "$" + n.toLocaleString(undefined, { maximumFractionDigits: 0 })
        : "—";

const MARKET_COLORS = {
    "Blue Ocean": "bg-emerald-100 text-emerald-900 border-emerald-500",
    Competitive: "bg-amber-100 text-amber-900 border-amber-500",
    Oversaturated: "bg-red-100 text-red-900 border-red-500",
};

const RECO_COLORS = {
    "Increase budget": "bg-klein text-white",
    "Improve Quality Score": "bg-amber-500 text-white",
    "Niche down": "bg-emerald-700 text-white",
};

// ---- Branded one-pager PDF generator ----
// A4 portrait. Emergent-brutalist look — solid klein header bar, ink-black type,
// monospaced meta labels, autoTable for the players list.
const KLEIN = [0, 0, 255];
const INK = [26, 26, 26];
const MUTED = [115, 115, 115];
const AMBER = [217, 119, 6];
const EMERALD = [4, 120, 87];
const RED = [185, 28, 28];

function marketColorRgb(type) {
    if (type === "Blue Ocean") return EMERALD;
    if (type === "Oversaturated") return RED;
    if (type === "Competitive") return AMBER;
    return INK;
}

function generateProspectingPdf(data, agency) {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const M = 36; // margin
    let y = 0;

    // Klein header bar
    doc.setFillColor(...KLEIN);
    doc.rect(0, 0, W, 90, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("MARKET INTELLIGENCE", M, 40);
    doc.setFontSize(10);
    doc.setFont("courier", "normal");
    doc.text(`// prepared by ${agency}`, M, 60);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    const dateStr = new Date().toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
    doc.text(dateStr, W - M, 40, { align: "right" });
    doc.setFont("courier", "normal");
    doc.setFontSize(9);
    doc.text("one-pager · confidential", W - M, 60, { align: "right" });
    y = 110;

    // Title
    doc.setTextColor(...INK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    const kwTitle = `"${data.keyword}"`;
    doc.text(kwTitle, M, y);
    y += 22;
    doc.setFont("courier", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    doc.text(`market · ${data.location} · ${data.language}`, M, y);
    y += 22;

    // Market stats row — 5 boxes across
    const m = data.market || {};
    const stats = [
        { label: "SEARCH VOL", value: (m.search_volume || 0).toLocaleString(), sub: "/ month" },
        { label: "AVG CPC", value: `$${(m.avg_cpc || 0).toFixed(2)}`, sub: "" },
        {
            label: "TOP OF PAGE",
            value: `$${(m.low_top_of_page_bid || 0).toFixed(2)}–$${(m.high_top_of_page_bid || 0).toFixed(2)}`,
            sub: "low – high",
        },
        { label: "COMPETITION", value: `${m.competition_index}/100`, sub: m.competition || "" },
        {
            label: "PAID ADS LIVE",
            value: String(data.paid_ads_detected || 0),
            sub: (data.paid_ads_detected || 0) > 0 ? "confirmed" : "none right now",
        },
    ];
    const boxW = (W - M * 2 - 6 * 4) / 5;
    stats.forEach((s, i) => {
        const x = M + i * (boxW + 6);
        doc.setDrawColor(...INK);
        doc.setLineWidth(1.4);
        doc.rect(x, y, boxW, 60, "S");
        doc.setFont("courier", "normal");
        doc.setFontSize(7);
        doc.setTextColor(...MUTED);
        doc.text(s.label, x + 6, y + 12);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.setTextColor(...INK);
        doc.text(s.value, x + 6, y + 34);
        if (s.sub) {
            doc.setFont("courier", "normal");
            doc.setFontSize(7);
            doc.setTextColor(...MUTED);
            doc.text(s.sub, x + 6, y + 50);
        }
    });
    y += 76;

    // AI verdict block
    const ai = data.ai || {};
    if (ai.market_type) {
        const mColor = marketColorRgb(ai.market_type);
        doc.setDrawColor(...INK);
        doc.setLineWidth(2);
        doc.rect(M, y, W - M * 2, 74, "S");
        // Left color chip
        doc.setFillColor(...mColor);
        doc.rect(M, y, 8, 74, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(...mColor);
        doc.text(ai.market_type.toUpperCase(), M + 16, y + 16);
        doc.setTextColor(...INK);
        doc.text(`→ ${ai.recommendation || ""}`.toUpperCase(), M + 16 + 90, y + 16);
        if (ai.market_leader?.domain) {
            doc.setFont("courier", "normal");
            doc.setFontSize(8);
            doc.setTextColor(...MUTED);
            doc.text(`market leader:`, W - M - 200, y + 16);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(...INK);
            doc.setFontSize(9);
            doc.text(ai.market_leader.domain, W - M - 8, y + 16, { align: "right" });
        }
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9.5);
        doc.setTextColor(...INK);
        const marketReason = doc.splitTextToSize(ai.market_reasoning || "", W - M * 2 - 26);
        doc.text(marketReason, M + 16, y + 34);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(...KLEIN);
        doc.text("RECOMMENDATION:", M + 16, y + 62);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...INK);
        const recoReason = doc.splitTextToSize(ai.recommendation_reason || "", W - M * 2 - 120);
        doc.text(recoReason, M + 16 + 90, y + 62);
        y += 92;
    }

    // Top 5 players table
    const rows = (data.advertisers || []).slice(0, 5).map((a, i) => [
        `${i + 1}`,
        a.domain || "",
        a.source === "paid" ? "PAID" : "ORGANIC",
        `${a.estimated_impression_share || 0}%`,
        `$${Math.round(a.estimated_monthly_spend || 0).toLocaleString()}`,
    ]);
    autoTable(doc, {
        startY: y,
        head: [["#", "DOMAIN", "SOURCE", "SHARE", "EST. SPEND / MO"]],
        body: rows,
        margin: { left: M, right: M },
        theme: "plain",
        styles: {
            font: "helvetica",
            fontSize: 9.5,
            textColor: INK,
            lineColor: [200, 200, 200],
            lineWidth: 0.6,
            cellPadding: 6,
        },
        headStyles: {
            fillColor: INK,
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 8,
            halign: "left",
        },
        columnStyles: {
            0: { cellWidth: 22, halign: "center", textColor: MUTED },
            2: { cellWidth: 55, halign: "center", fontSize: 8 },
            3: { halign: "right", fontStyle: "bold" },
            4: { halign: "right" },
        },
    });
    y = doc.lastAutoTable.finalY + 18;

    // Weakest competitor callout
    if (ai.weakest_competitor?.domain) {
        doc.setDrawColor(...AMBER);
        doc.setFillColor(255, 251, 235); // amber-50
        doc.setLineWidth(1.6);
        doc.rect(M, y, W - M * 2, 56, "FD");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(...AMBER);
        doc.text("EASIEST TO OUTRANK", M + 12, y + 16);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.setTextColor(...INK);
        doc.text(ai.weakest_competitor.domain, M + 12, y + 34);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...INK);
        const weakLines = doc.splitTextToSize(
            ai.weakest_competitor.reason || "",
            W - M * 2 - 24
        );
        doc.text(weakLines, M + 12, y + 48);
        y += 74;
    }

    // Pitch script
    if ((ai.pitch_script || []).length) {
        doc.setDrawColor(...KLEIN);
        doc.setLineWidth(2);
        doc.rect(M, y, W - M * 2, 12 + ai.pitch_script.length * 32 + 16, "S");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(...KLEIN);
        doc.text("COLD-OUTREACH PITCH SCRIPT", M + 12, y + 20);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9.5);
        doc.setTextColor(...INK);
        ai.pitch_script.forEach((p, i) => {
            const py = y + 44 + i * 32;
            doc.setFont("helvetica", "bold");
            doc.setTextColor(...KLEIN);
            doc.text(`${i + 1}.`, M + 14, py);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(...INK);
            const lines = doc.splitTextToSize(p, W - M * 2 - 40);
            doc.text(lines, M + 30, py);
        });
        y += 12 + ai.pitch_script.length * 32 + 26;
    }

    // Footer
    const pageH = doc.internal.pageSize.getHeight();
    doc.setDrawColor(...INK);
    doc.setLineWidth(0.6);
    doc.line(M, pageH - 40, W - M, pageH - 40);
    doc.setFont("courier", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text(
        `prepared by ${agency} · ${dateStr} · data via DataForSEO + Claude Sonnet 4.5`,
        M,
        pageH - 24
    );
    doc.text(
        "estimates directional — not affiliated with the listed advertisers",
        W - M,
        pageH - 24,
        { align: "right" }
    );

    const safe = (data.keyword || "market").replace(/[^\w-]+/g, "_").slice(0, 40);
    doc.save(`prospecting-${safe}.pdf`);
}


export const MarketResearch = () => {
    const [keyword, setKeyword] = useState("");
    const [language, setLanguage] = useState("en");
    const [languages, setLanguages] = useState([]);
    const [locQuery, setLocQuery] = useState("");
    const [locSuggestions, setLocSuggestions] = useState([]);
    const [locOpen, setLocOpen] = useState(false);
    const [locLoading, setLocLoading] = useState(false);
    const [selectedLoc, setSelectedLoc] = useState(null); // {id, canonical_name, target_type}
    const [country, setCountry] = useState("AU");
    const [countries, setCountries] = useState([]);
    // Persist agency name across runs — used for branded PDF header/footer.
    const [agencyName, setAgencyName] = useState(
        () => localStorage.getItem("mr_agency_name") || "Metroberry"
    );

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    useEffect(() => {
        keywordPlannerOptions()
            .then((opts) => {
                setLanguages(opts.languages || []);
                setCountries(opts.countries || []);
            })
            .catch(() => {});
    }, []);

    // Debounced geo suggest.
    useEffect(() => {
        const q = locQuery.trim();
        if (q.length < 2) {
            setLocSuggestions([]);
            return;
        }
        let cancelled = false;
        setLocLoading(true);
        const t = setTimeout(async () => {
            try {
                const r = await keywordPlannerGeoSuggest(q, country, language);
                if (!cancelled) {
                    setLocSuggestions(r.suggestions || []);
                    setLocOpen(true);
                }
            } catch (e) {
                if (!cancelled) setLocSuggestions([]);
            } finally {
                if (!cancelled) setLocLoading(false);
            }
        }, 350);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [locQuery, country, language]);

    const pickLoc = (loc) => {
        setSelectedLoc(loc);
        setLocQuery("");
        setLocSuggestions([]);
        setLocOpen(false);
    };

    const handleAnalyze = async () => {
        if (!keyword.trim()) {
            toast.error("Enter a keyword");
            return;
        }
        if (!selectedLoc) {
            toast.error("Pick a location — start typing a city or postcode");
            return;
        }
        setLoading(true);
        const t = toast.loading("Scanning the market via DataForSEO + Claude…");
        try {
            const r = await marketResearchAnalyze({
                keyword: keyword.trim(),
                location_name: selectedLoc.canonical_name || selectedLoc.name,
                language_code: language,
            });
            setData(r);
            toast.success(
                `Found ${r.advertisers?.length || 0} market players · $${(r.market?.avg_cpc || 0).toFixed(2)} CPC`,
                { id: t }
            );
        } catch (e) {
            const msg = errText(e, "Analysis failed");
            toast.error(msg, { id: t });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col flex-1 overflow-hidden bg-white">
            {/* Input bar */}
            <div className="border-b-2 border-ink px-6 py-5 space-y-3 bg-neutral-50">
                <div className="flex items-center gap-2">
                    <MagnifyingGlass size={18} weight="bold" className="text-klein" />
                    <h1 className="font-heading text-lg font-black uppercase tracking-tight text-ink">
                        Market Research & Prospecting
                    </h1>
                    <span className="mono-label text-neutral-500">
                        · DataForSEO + Claude · one call = one report
                    </span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                    <div className="lg:col-span-5">
                        <label className="mono-label mb-1 block">Keyword *</label>
                        <input
                            data-testid="mr-keyword"
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            placeholder='e.g. "end of lease cleaning"'
                            className="w-full h-9 text-sm font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-3"
                        />
                    </div>
                    <div className="lg:col-span-4 relative">
                        <label className="mono-label mb-1 block flex items-center gap-1">
                            <MapPin size={11} weight="bold" /> Location *
                            {selectedLoc && (
                                <span className="ml-auto text-[10px] font-mono normal-case text-klein truncate max-w-[65%]">
                                    {selectedLoc.canonical_name || selectedLoc.name}
                                </span>
                            )}
                        </label>
                        <input
                            data-testid="mr-location"
                            value={locQuery}
                            onChange={(e) => setLocQuery(e.target.value)}
                            onFocus={() => setLocOpen(true)}
                            onBlur={() => setTimeout(() => setLocOpen(false), 200)}
                            placeholder="Type a city, region, postcode…"
                            className="w-full h-9 text-sm font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-3"
                        />
                        {locLoading && (
                            <ArrowsClockwise
                                size={13}
                                weight="bold"
                                className="absolute right-3 bottom-2.5 text-neutral-400 animate-spin"
                            />
                        )}
                        {locOpen && locSuggestions.length > 0 && (
                            <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-ink shadow-lg max-h-64 overflow-y-auto">
                                {locSuggestions.map((s) => (
                                    <button
                                        key={s.id}
                                        data-testid={`mr-loc-${s.id}`}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            pickLoc(s);
                                        }}
                                        className="w-full text-left px-3 py-2 text-xs font-mono hover:bg-klein hover:text-white flex items-center justify-between gap-3 border-b border-neutral-200 last:border-b-0"
                                    >
                                        <span className="truncate">{s.canonical_name || s.name}</span>
                                        <span className="text-[10px] uppercase tracking-widest shrink-0 opacity-70">
                                            {s.target_type}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="lg:col-span-3 grid grid-cols-2 gap-2">
                        <div>
                            <label className="mono-label mb-1 block">Country</label>
                            <select
                                data-testid="mr-country"
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                                className="w-full h-9 text-xs font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-2"
                            >
                                {countries.map((c) => (
                                    <option key={c.code} value={c.code}>
                                        {c.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="mono-label mb-1 block">Language</label>
                            <select
                                data-testid="mr-language"
                                value={language}
                                onChange={(e) => setLanguage(e.target.value)}
                                className="w-full h-9 text-xs font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-2"
                            >
                                {languages.map((l) => (
                                    <option key={l.code} value={l.code}>
                                        {l.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div>
                    <button
                        data-testid="mr-analyze"
                        onClick={handleAnalyze}
                        disabled={loading || !keyword.trim() || !selectedLoc}
                        className="brutal-btn border border-klein bg-klein text-white font-mono uppercase tracking-widest text-[11px] font-bold px-4 h-9 flex items-center gap-2 disabled:opacity-40"
                    >
                        {loading ? (
                            <ArrowsClockwise size={13} weight="bold" className="animate-spin" />
                        ) : (
                            <Sparkle size={13} weight="bold" />
                        )}
                        Analyze market
                    </button>
                </div>
            </div>

            {/* Results */}
            {!data ? (
                <EmptyMR loading={loading} />
            ) : (
                <ResultsView
                    data={data}
                    agencyName={agencyName}
                    setAgencyName={(v) => {
                        setAgencyName(v);
                        try {
                            localStorage.setItem("mr_agency_name", v);
                        } catch (e) {
                            /* localStorage may be blocked in incognito */
                        }
                    }}
                />
            )}
        </div>
    );
};

const EmptyMR = ({ loading }) => (
    <div className="flex-1 flex items-center justify-center p-10 text-center">
        <div className="max-w-lg">
            <div className="mono-label mb-3">// market research</div>
            <h2 className="font-display text-3xl font-black tracking-tighter text-ink leading-tight">
                Scout any market <br />
                <span className="text-klein">in one click.</span>
            </h2>
            <p className="text-sm text-neutral-600 mt-4 leading-relaxed">
                Enter a keyword + location. We pull live search volume, average CPC, the top
                10 market players (paid + organic), and let Claude classify the market — Blue
                Ocean, Competitive, or Oversaturated — then generate a 3-point cold-outreach
                pitch script personalized to the data.
            </p>
            {loading && (
                <div className="mt-6 flex justify-center gap-1">
                    {[0, 1, 2, 3].map((i) => (
                        <span
                            key={i}
                            className="inline-block w-1.5 h-6 bg-klein"
                            style={{
                                animation: `bar-slide 1s ease-in-out ${i * 0.12}s infinite alternate`,
                                opacity: 0.6,
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    </div>
);

const ResultsView = ({ data, agencyName, setAgencyName }) => {
    const m = data.market || {};
    const ai = data.ai || {};
    const ads = data.advertisers || [];
    const marketClass = MARKET_COLORS[ai.market_type] || "bg-neutral-200 text-ink border-neutral-400";
    const recoClass = RECO_COLORS[ai.recommendation] || "bg-neutral-800 text-white";
    const chartData = useMemo(
        () =>
            ads.slice(0, 4).map((a, i) => ({
                name: a.domain.length > 22 ? a.domain.slice(0, 20) + "…" : a.domain,
                share: a.estimated_impression_share || 0,
                spend: a.estimated_monthly_spend || 0,
                source: a.source,
                highlight: i === 0,
            })),
        [ads]
    );

    const downloadPdf = () => {
        try {
            generateProspectingPdf(data, agencyName || "Your Agency");
            toast.success("PDF downloaded");
        } catch (e) {
            toast.error("PDF generation failed");
            console.error(e);
        }
    };

    return (
        <div className="flex-1 overflow-y-auto thin-scroll">
            <div className="p-6 space-y-6">
                {/* Header actions row */}
                <div className="flex items-center flex-wrap gap-2">
                    <div className="mono-label text-neutral-500">
                        Report for <span className="font-bold text-ink normal-case">&quot;{data.keyword}&quot;</span>
                        <span className="mx-1">·</span>
                        {data.location}
                    </div>
                    <div className="flex-1" />
                    <label className="flex items-center gap-1 border border-neutral-300 bg-white px-2 h-8">
                        <span className="mono-label text-neutral-500">Agency:</span>
                        <input
                            data-testid="mr-agency-name"
                            value={agencyName}
                            onChange={(e) => setAgencyName(e.target.value)}
                            placeholder="Your agency"
                            className="w-32 text-xs font-mono bg-white focus:outline-none"
                        />
                    </label>
                    <button
                        data-testid="mr-download-pdf"
                        onClick={downloadPdf}
                        className="brutal-btn h-8 px-3 border border-ink bg-ink text-white hover:bg-klein hover:border-klein font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1"
                    >
                        <FilePdf size={12} weight="bold" /> Download branded PDF
                    </button>
                </div>
                {/* Market stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <BigStat label="Search volume" value={m.search_volume?.toLocaleString() || "—"} sub="per month" />
                    <BigStat label="Avg. CPC" value={`$${(m.avg_cpc || 0).toFixed(2)}`} />
                    <BigStat
                        label="Top-of-page bid"
                        value={`$${(m.low_top_of_page_bid || 0).toFixed(2)} – $${(m.high_top_of_page_bid || 0).toFixed(2)}`}
                        sub="low – high"
                    />
                    <BigStat label="Competition" value={`${m.competition_index}/100`} sub={m.competition || "—"} />
                    <BigStat
                        label="Paid ads live"
                        value={data.paid_ads_detected || 0}
                        sub={data.paid_ads_detected ? "confirmed advertisers" : "none right now"}
                    />
                </div>

                {/* AI verdict */}
                {ai.market_type && (
                    <div className="border-2 border-ink p-4 space-y-3">
                        <div className="flex items-center flex-wrap gap-2">
                            <span
                                data-testid="mr-market-type"
                                className={`px-2 py-1 text-[11px] font-mono uppercase tracking-widest font-bold border-2 ${marketClass}`}
                            >
                                {ai.market_type}
                            </span>
                            <span
                                className={`px-2 py-1 text-[11px] font-mono uppercase tracking-widest font-bold ${recoClass}`}
                            >
                                → {ai.recommendation}
                            </span>
                            <div className="flex-1" />
                            {ai.market_leader?.domain && (
                                <span className="mono-label flex items-center gap-1">
                                    <Trophy size={11} weight="bold" className="text-amber-500" />
                                    Leader:{" "}
                                    <span className="font-bold text-ink normal-case">
                                        {ai.market_leader.domain}
                                    </span>
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-ink">{ai.market_reasoning}</p>
                        <p className="text-xs font-mono text-neutral-600 italic">
                            <span className="font-bold not-italic">Recommendation:</span> {ai.recommendation_reason}
                        </p>
                    </div>
                )}

                {/* Bar chart + weakest competitor */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 border border-neutral-300 bg-white">
                        <div className="px-4 py-2 border-b border-neutral-300 bg-neutral-50 flex items-center gap-2">
                            <ChartBar size={14} weight="bold" className="text-klein" />
                            <span className="mono-label">Estimated Impression Share — top 4</span>
                        </div>
                        <div className="p-4" style={{ height: 260 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 40 }}>
                                    <XAxis type="number" hide domain={[0, "dataMax"]} />
                                    <YAxis
                                        type="category"
                                        dataKey="name"
                                        tick={{ fill: "#1a1a1a", fontFamily: "monospace", fontSize: 11 }}
                                        width={150}
                                    />
                                    <RTooltip
                                        formatter={(v, k) =>
                                            k === "share" ? `${v}%` : k === "spend" ? currency(v) : v
                                        }
                                        contentStyle={{
                                            border: "2px solid #1a1a1a",
                                            borderRadius: 0,
                                            fontFamily: "monospace",
                                            fontSize: 12,
                                        }}
                                    />
                                    <Bar dataKey="share" name="Impression share (%)" barSize={26}>
                                        {chartData.map((d, i) => (
                                            <Cell
                                                key={i}
                                                fill={d.highlight ? "#0000ff" : d.source === "paid" ? "#dc2626" : "#525252"}
                                            />
                                        ))}
                                        <LabelList
                                            dataKey="share"
                                            position="right"
                                            formatter={(v) => `${v}%`}
                                            style={{ fill: "#1a1a1a", fontFamily: "monospace", fontSize: 11, fontWeight: 700 }}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    {ai.weakest_competitor?.domain && (
                        <div className="border-2 border-amber-500 bg-amber-50 p-4">
                            <div className="mono-label text-amber-900 flex items-center gap-1 mb-2">
                                <Warning size={12} weight="bold" /> Weakest competitor
                            </div>
                            <div className="font-heading text-xl font-black text-ink leading-tight break-all">
                                {ai.weakest_competitor.domain}
                            </div>
                            <p className="text-xs font-mono text-amber-900 mt-2 leading-relaxed">
                                {ai.weakest_competitor.reason}
                            </p>
                        </div>
                    )}
                </div>

                {/* Advertisers table */}
                <div className="border border-neutral-300 overflow-hidden">
                    <div className="px-4 py-2 border-b border-neutral-300 bg-neutral-50 mono-label">
                        Top 10 market players ({data.advertisers?.length || 0})
                    </div>
                    <div className="max-h-96 overflow-y-auto thin-scroll">
                        <table className="w-full text-sm">
                            <thead className="bg-white sticky top-0">
                                <tr className="border-b border-neutral-300">
                                    <th className="px-3 py-2 text-left mono-label w-10">#</th>
                                    <th className="px-3 py-2 text-left mono-label">Domain</th>
                                    <th className="px-3 py-2 text-left mono-label">Ad / snippet</th>
                                    <th className="px-3 py-2 text-right mono-label">Share</th>
                                    <th className="px-3 py-2 text-right mono-label">Est. spend / mo</th>
                                    <th className="px-3 py-2 text-center mono-label">Source</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ads.map((a, i) => (
                                    <tr
                                        key={a.domain}
                                        data-testid={`mr-adv-${a.domain}`}
                                        className={`border-b border-neutral-200 hover:bg-neutral-50 ${
                                            i === 0 ? "bg-klein/5" : ""
                                        }`}
                                    >
                                        <td className="px-3 py-2 font-mono text-neutral-500">{i + 1}</td>
                                        <td className="px-3 py-2 font-bold text-ink">
                                            <a
                                                href={a.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="hover:text-klein flex items-center gap-1 group"
                                            >
                                                {a.domain}
                                                <ArrowUpRight
                                                    size={11}
                                                    className="opacity-0 group-hover:opacity-100"
                                                />
                                            </a>
                                            {a.also_ranks_organic && a.source === "paid" && (
                                                <span className="ml-1 px-1 py-0.5 text-[9px] font-mono uppercase tracking-widest font-bold bg-purple-100 text-purple-900 border border-purple-300">
                                                    dual
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-neutral-700 text-xs max-w-[300px] truncate">
                                            {a.title || "—"}
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-ink font-bold">
                                            {a.estimated_impression_share || 0}%
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-ink">
                                            {currency(a.estimated_monthly_spend)}
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            <span
                                                className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest font-bold border ${
                                                    a.source === "paid"
                                                        ? "bg-red-100 text-red-900 border-red-300"
                                                        : "bg-neutral-100 text-neutral-700 border-neutral-300"
                                                }`}
                                            >
                                                {a.source}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pitch script */}
                {ai.pitch_script?.length > 0 && (
                    <PitchScript points={ai.pitch_script} keyword={data.keyword} />
                )}

                {/* Notice on data-source */}
                {(data.paid_ads_detected || 0) === 0 && ads.length > 0 && (
                    <div className="border border-neutral-300 bg-neutral-50 p-3 flex items-start gap-2 text-[11px] text-neutral-600 font-mono">
                        <Info size={12} className="mt-0.5 shrink-0" />
                        <span>
                            Google didn&apos;t show any paid ads for this SERP at scrape time. The
                            table above lists the top organic domains — they&apos;re the players
                            competing in this space. Shares are estimated from CTR-weighted
                            positions. For 100% accurate Auction Insights, upload the CSV export
                            from your client&apos;s Google Ads UI in the Performance tab.
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

const PitchScript = ({ points, keyword }) => {
    const [copied, setCopied] = useState(false);
    const text = points
        .map((p, i) => `${i + 1}. ${p}`)
        .join("\n\n");
    const copyAll = async () => {
        try {
            await navigator.clipboard.writeText(
                `Pitch script — ${keyword}\n\n${text}`
            );
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
            toast.success("Pitch copied");
        } catch (e) {
            toast.error("Copy failed");
        }
    };
    return (
        <div className="border-2 border-klein bg-klein/5 p-4">
            <div className="flex items-center gap-2 mb-3">
                <Sparkle size={14} weight="bold" className="text-klein" />
                <span className="font-heading font-black text-ink text-base">
                    Cold-outreach pitch script
                </span>
                <button
                    data-testid="mr-copy-pitch"
                    onClick={copyAll}
                    className="ml-auto brutal-btn h-7 px-2 border border-ink bg-white text-ink hover:bg-ink hover:text-white font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1"
                >
                    <Copy size={11} weight="bold" /> {copied ? "Copied" : "Copy all"}
                </button>
            </div>
            <ol className="space-y-2">
                {points.map((p, i) => (
                    <li
                        key={i}
                        data-testid={`mr-pitch-${i}`}
                        className="flex gap-3 text-sm text-ink leading-relaxed"
                    >
                        <span className="font-heading font-black text-klein text-lg leading-none w-6 shrink-0">
                            {i + 1}.
                        </span>
                        <span>{p}</span>
                    </li>
                ))}
            </ol>
        </div>
    );
};

const BigStat = ({ label, value, sub }) => (
    <div className="border-2 border-ink bg-white px-3 py-2">
        <div className="mono-label text-neutral-500">{label}</div>
        <div className="font-heading text-xl font-black text-ink leading-tight">{value}</div>
        {sub && <div className="text-[10px] font-mono text-neutral-500 mt-0.5">{sub}</div>}
    </div>
);
